/**
 * Custom hook for fetching KPI data
 */

import { useState, useEffect, useCallback } from 'react';
import { KpiData, KpiError, UseKpiDataReturn } from '@/types/kpi';
import axios from 'axios';

// Default KPI data structure with all 10 KPIs
const getDefaultKpiData = (sellerId: string, periodStart: string, periodEnd: string): KpiData => ({
  seller_id: sellerId,
  period: {
    start: periodStart,
    end: periodEnd
  },
  care_stays_started: 0,
  active_care_stays: 0,
  care_days: 0,
  monthly_revenue_earned: 0,
  lead_to_contract_cvr: 0,
  paused_customers: 0,
  lead_to_eb_cvr: 0,
  eb_to_posting_cvr: 0,
  posting_to_contract_cvr: 0,
  termination_rate: 0,
  active_customers: 0,
  calculated_at: undefined,
});

export function useKpiData(
  sellerId: string,
  periodStart: string,
  periodEnd: string
): UseKpiDataReturn {
  const [data, setData] = useState<KpiData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchKpiData = useCallback(async () => {
    if (!sellerId || !periodStart || !periodEnd) {
      setError(new Error('Missing required parameters'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Configure API base URL for production vs development
      const apiBaseUrl = import.meta.env.PROD 
        ? 'https://xora-v2-backend.onrender.com'
        : ''; // Use proxy in development
      
      const url = `${apiBaseUrl}/kpi/${sellerId}?from=${periodStart}&to=${periodEnd}`;
      
      console.log('🔍 KPI fetch:', { sellerId, periodStart, periodEnd, url });
      
      const response = await axios.get<KpiData>(url);
      
      // Merge response with defaults to ensure all KPIs are present
      const defaultData = getDefaultKpiData(sellerId, periodStart, periodEnd);
      const mergedData = {
        ...defaultData,
        ...response.data,
        // Ensure numeric values are numbers, not null
        care_stays_started: response.data.care_stays_started ?? 0,
        active_care_stays: response.data.active_care_stays ?? 0,
        care_days: response.data.care_days ?? 0,
        monthly_revenue_earned: response.data.monthly_revenue_earned ?? 0,
        lead_to_contract_cvr: response.data.lead_to_contract_cvr ?? 0,
        paused_customers: response.data.paused_customers ?? 0,
        lead_to_eb_cvr: response.data.lead_to_eb_cvr ?? 0,
        eb_to_posting_cvr: response.data.eb_to_posting_cvr ?? 0,
        posting_to_contract_cvr: response.data.posting_to_contract_cvr ?? 0,
        termination_rate: response.data.termination_rate ?? 0,
        active_customers: response.data.active_customers ?? 0,
      };
      
      setData(mergedData);
      setError(null);
    } catch (err) {
      console.error('🚨 KPI fetch error:', {
        sellerId,
        periodStart,
        periodEnd,
        error: err
      });
      
      let errorMessage = 'Failed to fetch KPI data';
      
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          // On 404, show empty data instead of error
          console.log('No KPI data found, showing zeros');
          setData(getDefaultKpiData(sellerId, periodStart, periodEnd));
          setError(null);
          setIsLoading(false);
          return;
        } else {
          errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message;
        }
      }
      
      setError(new Error(errorMessage));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [sellerId, periodStart, periodEnd]);

  useEffect(() => {
    fetchKpiData();
  }, [fetchKpiData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchKpiData
  };
}

/**
 * Format period for display
 */
export function formatPeriod(periodStart: string, periodEnd: string): string {
  const startDate = new Date(periodStart);
  const endDate = new Date(periodEnd);
  
  const options: Intl.DateTimeFormatOptions = { 
    month: 'long', 
    year: 'numeric' 
  };
  
  // If same month and year, show only once
  if (startDate.getMonth() === endDate.getMonth() && 
      startDate.getFullYear() === endDate.getFullYear()) {
    return startDate.toLocaleDateString('en-US', options);
  }
  
  // Otherwise show range
  const startStr = startDate.toLocaleDateString('en-US', { month: 'short' });
  const endStr = endDate.toLocaleDateString('en-US', options);
  
  return `${startStr} - ${endStr}`;
}